import { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  isSameDay,
  eachDayOfInterval,
  setHours,
  setMinutes,
  getHours,
  getMinutes,
  isAfter,
  isBefore,
  startOfDay,
  parse,
  isValid,
  set,
  getYear,
} from "date-fns";
import { IMaskInput } from "react-imask";
import IMask from "imask";
import { clsx } from "clsx";
import { DropDownIcon } from "../../icons/inputIcons";
import Btn from "../button/Btn";
import IconBtn from "../button/IconBtn";

export function cn(...inputs) {
  return clsx(...inputs);
}

export default function DateTimePicker({
  value,
  onChange,
  label = "Uncontrolled picker",
  placeholder = "DD/MM/YYYY hh:mm aa",
  min,
  max,
  menuPlacement = "bottom", // "top" | "bottom"
  ...rest
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (!value) return new Date();
    const d = value instanceof Date ? value : new Date(value);
    return isValid(d) ? d : new Date();
  });
  const [stagedDate, setStagedDate] = useState(() => {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    return isValid(d) ? d : null;
  });
  const [inputValue, setInputValue] = useState(() => {
    if (!value) return "";
    const d = value instanceof Date ? value : new Date(value);
    return isValid(d) ? format(d, "dd/MM/yyyy hh:mm aa") : "";
  });
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [activePlacement, setActivePlacement] = useState(menuPlacement);

  const calculatePosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const scrollLeft = window.scrollX || document.documentElement.scrollLeft;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;

      const dropdownHeight = 380;
      const dropdownWidth =
        window.innerWidth < 600 ? window.innerWidth * 0.9 : 550;

      // Auto-flipping logic
      let placement = menuPlacement;
      if (
        menuPlacement === "bottom" &&
        rect.bottom + dropdownHeight > window.innerHeight &&
        rect.top > dropdownHeight
      ) {
        placement = "top";
      } else if (
        menuPlacement === "top" &&
        rect.top < dropdownHeight &&
        window.innerHeight - rect.bottom > dropdownHeight
      ) {
        placement = "bottom";
      }
      setActivePlacement(placement);

      let top =
        placement === "top" ? rect.top + scrollTop : rect.bottom + scrollTop;
      let left = rect.left + scrollLeft;

      // Ensure it stays within horizontal bounds
      if (left + dropdownWidth > window.innerWidth) {
        left = window.innerWidth - dropdownWidth - 10;
      }
      if (left < 10) left = 10;

      setCoords({ top, left });
    }
  }, [menuPlacement]);

  useEffect(() => {
    if (isOpen) {
      calculatePosition();
      const handleScroll = (e) => {
        // Don't re-calculate if scrolling inside the dropdown itself
        if (dropdownRef.current && dropdownRef.current.contains(e.target)) {
          return;
        }
        calculatePosition();
      };

      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", calculatePosition);

      return () => {
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", calculatePosition);
      };
    }
  }, [isOpen, calculatePosition]);

  const resetInternalState = useCallback(() => {
    if (!value) {
      const now = new Date();
      setStagedDate(now);
      setInputValue(format(now, "dd/MM/yyyy hh:mm aa"));
      setCurrentMonth(now);
    } else {
      const d = value instanceof Date ? value : new Date(value);
      if (isValid(d)) {
        setStagedDate(d);
        setInputValue(format(d, "dd/MM/yyyy hh:mm aa"));
        setCurrentMonth(d);
      }
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isInsideContainer =
        containerRef.current && containerRef.current.contains(event.target);
      const isInsideDropdown =
        dropdownRef.current && dropdownRef.current.contains(event.target);

      if (!isInsideContainer && !isInsideDropdown) {
        setIsOpen(false);
        resetInternalState();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [resetInternalState]);

  const lastSyncedValueRef = useRef(null);

  useEffect(() => {
    if (!value) {
      if (lastSyncedValueRef.current !== null) {
        setStagedDate(null);
        setInputValue("");
        lastSyncedValueRef.current = null;
      }
      return;
    }

    const dateValue = value instanceof Date ? value : new Date(value);
    if (!isValid(dateValue)) return;

    const timeValue = dateValue.getTime();
    if (timeValue !== lastSyncedValueRef.current) {
      setStagedDate(dateValue);
      setInputValue(format(dateValue, "dd/MM/yyyy hh:mm aa"));
      setCurrentMonth(dateValue);
      lastSyncedValueRef.current = timeValue;
    }
  }, [value]);

  const handleDateSelect = (date) => {
    const current = stagedDate || new Date();
    const newDate = set(date, {
      hours: getHours(current),
      minutes: getMinutes(current),
    });
    setStagedDate(newDate);
    setInputValue(format(newDate, "dd/MM/yyyy hh:mm aa"));
  };

  const handleTimeSelect = (hours, minutes, ampm) => {
    const baseDate = stagedDate || new Date();
    let finalHours = hours;
    if (ampm === "PM" && hours < 12) finalHours += 12;
    if (ampm === "AM" && hours === 12) finalHours = 0;

    const newDate = setHours(setMinutes(baseDate, minutes), finalHours);
    setStagedDate(newDate);
    setInputValue(format(newDate, "dd/MM/yyyy hh:mm aa"));
  };

  const handleMaskAccept = (val) => {
    setInputValue(val);
    if (val.length === 19) {
      const parsedDate = parse(val, "dd/MM/yyyy hh:mm aa", new Date());
      if (isValid(parsedDate)) {
        const isBeforeMin = min && isBefore(parsedDate, min);
        const isAfterMax = max && isAfter(parsedDate, max);

        if (!isBeforeMin && !isAfterMax) {
          setStagedDate(parsedDate);
          setCurrentMonth(parsedDate);
        }
      }
    }
  };

  const handleOpen = () => {
    calculatePosition();
    resetInternalState();
    setIsOpen(true);
  };

  const handleOk = () => {
    if (stagedDate && onChange) {
      if (rest.name) {
        onChange({
          target: {
            name: rest.name,
            value: stagedDate,
          },
        });
      } else {
        onChange(stagedDate);
      }
    }
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
    resetInternalState();
  };

  return (
    <div className="position-relative" ref={containerRef}>
      <div onClick={handleOpen}>
        <IMaskInput
          mask="DD/MM/YYYY HH:mm aa"
          blocks={{
            DD: {
              mask: IMask.MaskedRange,
              from: 1,
              to: 31,
            },
            MM: {
              mask: IMask.MaskedRange,
              from: 1,
              to: 12,
            },
            YYYY: {
              mask: IMask.MaskedRange,
              from: 2000,
              to: 9999,
            },
            HH: {
              mask: IMask.MaskedRange,
              from: 1,
              to: 12,
            },
            mm: {
              mask: IMask.MaskedRange,
              from: 0,
              to: 59,
            },
            aa: {
              mask: IMask.MaskedEnum,
              enum: ["AM", "PM", "am", "pm"],
            },
          }}
          value={inputValue}
          unmask={false}
          onAccept={handleMaskAccept}
          placeholder={placeholder}
          autoComplete="off"
          spellCheck="false"
          autoCorrect="off"
          {...rest}
        />
      </div>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            className="position-absolute bg-white border rounded-3 shadow-sm overflow-hidden"
            style={{
              width: window.innerWidth < 400 ? "80vw" : "400px",
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              transform:
                activePlacement === "top" ? "translateY(-100%)" : "none",
              marginTop: activePlacement === "top" ? "-10px" : "4px",
              zIndex: 9999,
              visibility: coords.top === 0 ? "hidden" : "visible",
            }}
          >
            <div
              className="grid gap-0"
              style={{ height: window.innerWidth < 600 ? "auto" : "240px" }}
            >
              {/* Calendar Section */}
              <div className="g-col-7 p-4 pe-2 border-end">
                <CalendarView
                  currentMonth={currentMonth}
                  setCurrentMonth={setCurrentMonth}
                  selectedDate={stagedDate}
                  onSelect={handleDateSelect}
                  min={min}
                  max={max}
                />
              </div>
              {/* Time Section */}
              <TimeView
                selectedDate={stagedDate}
                onSelect={handleTimeSelect}
                min={min}
                max={max}
              />
            </div>
            {/* Footer */}
            <div className="px-4 py-2 g-col-12 flex-between gap-3 border-top">
              <Btn
                title="Today"
                onClick={() => {
                  const now = new Date();
                  setStagedDate(now);
                  setInputValue(format(now, "dd/MM/yyyy hh:mm aa"));
                  setCurrentMonth(now);
                }}
                variant="outline-primary"
                className="py-2"
              />
              <div className="d-flex gap-3">
                <Btn
                  title="Cancel"
                  onClick={handleCancel}
                  variant="cancel"
                  className="py-2"
                />
                <Btn
                  title="Ok"
                  onClick={handleOk}
                  variant="primary"
                  className="py-2"
                />
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

function CalendarView({
  currentMonth,
  setCurrentMonth,
  selectedDate,
  onSelect,
  min,
  max,
}) {
  const [view, setView] = useState("days");

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);

  const calendarDays = eachDayOfInterval({ start: startDate, end: endDate });
  const weekDays = ["S", "M", "T", "W", "T", "F", "S"];

  const years = Array.from(
    { length: 20 },
    (_, i) => getYear(currentMonth) - 7 + i,
  );

  return (
    <div className="h-100 d-flex flex-column">
      <div className="flex-between mb-3">
        <Btn
          className="p-0 text-decoration-none"
          variant="link"
          onClick={() => setView(view === "days" ? "years" : "days")}
          title={
            view === "days" ? format(currentMonth, "MMMM yyyy") : "Select Year"
          }
          endIcon={
            <DropDownIcon
              size={20}
              className={`text-secondary transition ${view === "years" ? "rotate-180" : ""}`}
            />
          }
        />

        <div className="d-flex gap-3">
          <IconBtn
            onClick={() =>
              setCurrentMonth(subMonths(currentMonth, view === "days" ? 1 : 12))
            }
            variant="link"
            className="p-0 text-secondary rotate-90"
            icon={<DropDownIcon size={20} />}
          />

          <IconBtn
            onClick={() =>
              setCurrentMonth(addMonths(currentMonth, view === "days" ? 1 : 12))
            }
            variant="link"
            className="p-0 text-secondary -rotate-90"
            icon={<DropDownIcon size={20} />}
          />
        </div>
      </div>

      {view === "days" ? (
        <div
          className="d-grid gap-1"
          style={{ gridTemplateColumns: "repeat(7, 1fr)" }}
        >
          {weekDays.map((day, i) => (
            <span key={i} className="text-center text-muted fw-medium fs-xs">
              {day}
            </span>
          ))}
          {calendarDays.map((day, idx) => {
            const isToday = isSameDay(day, new Date());
            const isSelected = selectedDate && isSameDay(day, selectedDate);
            const isDisabled =
              (min && isBefore(day, startOfDay(min))) ||
              (max && isAfter(day, startOfDay(max)));

            return (
              <Btn
                key={idx}
                onClick={() => !isDisabled && onSelect(day)}
                disabled={isDisabled}
                variant="primary-02"
                className={`ratio-1 fs-xs rounded-circle p-0 flex-center lh-1 ${isDisabled ? "border-0" : ""} ${isToday && !isSelected ? "border border-primary-02" : ""}`}
                active={isSelected}
                style={{
                  width: "28px",
                  ...(isToday &&
                    !isSelected && {
                      backgroundColor: "rgba(var(--bs-primary-rgb), 0.05)",
                    }),
                }}
                title={format(day, "d")}
              />
            );
          })}
        </div>
      ) : (
        <div
          className="d-grid columns-3 gap-2 overflow-y-auto custom-scroll pe-2"
          style={{ maxHeight: "196px" }}
        >
          {years.map((year) => {
            const isCurrentYear = getYear(currentMonth) === year;
            const isDisabled =
              (min && year < getYear(min)) || (max && year > getYear(max));

            return (
              <>
                <Btn
                  key={year}
                  onClick={() => {
                    if (!isDisabled) {
                      const newDate = new Date(currentMonth);
                      newDate.setFullYear(year);
                      setCurrentMonth(newDate);
                      setView("days");
                    }
                  }}
                  variant="primary-02"
                  active={isCurrentYear}
                  disabled={isDisabled}
                  className="py-2 text-sm"
                  title={year}
                />
              </>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TimeView({ selectedDate, onSelect, min, max }) {
  const isMinDay = selectedDate && min && isSameDay(selectedDate, min);
  const isMaxDay = selectedDate && max && isSameDay(selectedDate, max);
  const hours = Array.from({ length: 12 }, (_, i) => (i === 0 ? 12 : i));
  const minutes = Array.from({ length: 12 }, (_, i) => i * 5);

  const rawHours = selectedDate ? getHours(selectedDate) : 12;
  const selectedHour = rawHours % 12 === 0 ? 12 : rawHours % 12;
  const selectedMinute = selectedDate
    ? Math.floor(getMinutes(selectedDate) / 5) * 5
    : 0;
  const selectedAMPM = rawHours >= 12 ? "PM" : "AM";

  return (
    <div className="g-col-5 grid columns-3 gap-0 overflow-hidden">
      {/* Hours Column */}
      <div className="overflow-y-auto custom-scroll p-2 overflow-x-hidden mb-n2">
        {hours.map((h) => {
          let finalH = h;
          if (selectedAMPM === "PM" && h < 12) finalH += 12;
          if (selectedAMPM === "AM" && h === 12) finalH = 0;

          const isDisabled =
            (isMinDay && finalH < getHours(min)) ||
            (isMaxDay && finalH > getHours(max));

          return (
            <Btn
              key={h}
              onClick={() =>
                !isDisabled && onSelect(h, selectedMinute, selectedAMPM)
              }
              variant="outline-primary"
              className="fs-xs py-3 mb-2 w-100"
              disabled={isDisabled}
              active={selectedHour === h}
              title={h.toString().padStart(2, "0")}
            />
          );
        })}
      </div>

      {/* Minutes Column */}
      <div className="overflow-y-auto custom-scroll p-2 overflow-x-hidden mb-n2">
        {minutes.map((m) => {
          let finalH = selectedHour;
          if (selectedAMPM === "PM" && selectedHour < 12) finalH += 12;
          if (selectedAMPM === "AM" && selectedHour === 12) finalH = 0;

          const isCurrentHourMin = isMinDay && finalH === getHours(min);
          const isCurrentHourMax = isMaxDay && finalH === getHours(max);
          const isDisabled =
            (isCurrentHourMin && m < getMinutes(min)) ||
            (isCurrentHourMax && m > getMinutes(max));

          return (
            <Btn
              key={m}
              onClick={() =>
                !isDisabled && onSelect(selectedHour, m, selectedAMPM)
              }
              variant="outline-primary"
              className="fs-xs py-3 mb-2 w-100"
              disabled={isDisabled}
              active={selectedMinute === m}
              title={m.toString().padStart(2, "0")}
            />
          );
        })}
      </div>

      {/* AM/PM Column */}
      <div className="p-2">
        {["AM", "PM"].map((opt) => {
          const amDisabled = isMinDay && getHours(min) >= 12;
          const pmDisabled = isMaxDay && getHours(max) < 12;
          const itemDisabled =
            (opt === "AM" && amDisabled) || (opt === "PM" && pmDisabled);

          return (
            <Btn
              key={opt}
              onClick={() =>
                !itemDisabled && onSelect(selectedHour, selectedMinute, opt)
              }
              variant="outline-primary"
              className="fs-xs py-3 mb-2"
              disabled={itemDisabled}
              active={selectedAMPM === opt}
              title={opt}
            />
          );
        })}
      </div>
    </div>
  );
}
